import { useState, type CSSProperties, type MouseEvent, type ReactElement } from "react";

import type { NodeDetail } from "./api";
import {
  classifyNode,
  orphanedGuests,
  physicalNodeStatus,
  physicalNodes,
  type FleetNode,
  type NodeStatus,
  summarizeFleet,
} from "./domain";
import { tokens } from "./tokens";
import { analyzeServiceTopology } from "./operations";

type PercentageStyle = CSSProperties & { readonly "--value": number };
type SegmentStyle = CSSProperties & { readonly "--segment": number };

interface NodeSummaryCardsProps {
  readonly nodes: readonly FleetNode[];
  readonly now: Date;
  readonly onOpen: (
    event: MouseEvent<HTMLAnchorElement>,
    node: FleetNode,
  ) => void;
}

const statusOrder = tokens.status as readonly NodeStatus[];
const observationPageSize = 20;

/** Browse the most recent signed health samples returned by the bounded detail API. */
export function ObservationHistory({ detail }: { readonly detail: NodeDetail }) {
  const [page, setPage] = useState(1);
  const total = detail.observations.length;
  const pages = Math.max(1, Math.ceil(total / observationPageSize));
  const currentPage = Math.min(page, pages);
  const start = (currentPage - 1) * observationPageSize;
  const rows = detail.observations.slice(start, start + observationPageSize);
  return (
    <section className="observation-history" aria-labelledby="observation-history-title">
      <div className="section-heading">
        <div>
          <h3 id="observation-history-title">Observation history</h3>
          <p>Retained health samples, newest first. These are measurements, not separate alerts.</p>
        </div>
        <span>{total} recent</span>
      </div>
      {total === 0
        ? <p>No health observations have been received for this node.</p>
        : (
          <>
            <div className="table-scroll">
              <table>
                <caption className="sr-only">Recent observations for this node</caption>
                <thead><tr><th scope="col">Observed</th><th scope="col">Sequence</th><th scope="col">Health</th><th scope="col">Disk</th><th scope="col">Memory</th></tr></thead>
                <tbody>
                  {rows.map((observation) => (
                    <tr key={observation.sequence}>
                      <td><time dateTime={observation.observed_at}>{new Date(observation.observed_at).toLocaleString()}</time></td>
                      <td>{observation.sequence}</td>
                      <td>{observation.health}</td>
                      <td>{observation.disk_used_percent}%</td>
                      <td>{observation.memory_used_percent === null ? "Unavailable" : `${observation.memory_used_percent}%`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {pages > 1
              ? (
                <nav className="observation-history__pagination" aria-label="Observation pages">
                  <button type="button" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Previous</button>
                  <span>Page {currentPage} of {pages}</span>
                  <button type="button" disabled={currentPage === pages} onClick={() => setPage(currentPage + 1)}>Next</button>
                </nav>
              )
              : null}
          </>
        )}
    </section>
  );
}

/** Render current fleet health as an accessible segmented pie chart. */
export function HealthPie(
  { nodes, now }: { readonly nodes: readonly FleetNode[]; readonly now: Date },
) {
  const summary = summarizeFleet(nodes, now);
  const total = Math.max(summary.total, 1);
  const {
    center,
    circumference,
    descriptorLabelY,
    radius,
    totalLabelY,
    viewBoxSize,
  } = tokens.chart;
  let offset = 0;
  return (
    <figure className="chart chart--pie">
      <figcaption>
        <h3>Fleet health</h3>
        <p>Physical hosts counted once; guest faults are included.</p>
      </figcaption>
      <div className="pie-layout">
        <svg
          className="pie"
          viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
          role="img"
          aria-label={`${summary.healthy} healthy of ${summary.total} physical hosts`}
        >
          <circle className="pie-track" cx={center} cy={center} r={radius} />
          {statusOrder.map((status) => {
            const count = summary[status];
            const length = (count / total) * circumference;
            const segment = (
              <circle
                key={status}
                className={`pie-segment pie-segment--${status}`}
                cx={center}
                cy={center}
                r={radius}
                strokeDasharray={`${length} ${circumference - length}`}
                strokeDashoffset={-offset}
              />
            );
            offset += length;
            return segment;
          })}
          <text
            x={center}
            y={totalLabelY}
            textAnchor="middle"
            className="pie-total"
          >
            {summary.total}
          </text>
          <text
            x={center}
            y={descriptorLabelY}
            textAnchor="middle"
            className="pie-label"
          >
            hosts
          </text>
        </svg>
        <ul className="legend">
          {statusOrder.map((status) => (
            <li key={status}>
              <span className={`status status--${status}`} aria-hidden="true" />
              <span>{status}</span>
              <strong>{summary[status]}</strong>
            </li>
          ))}
        </ul>
      </div>
    </figure>
  );
}

/** Render compact node summaries that open each node's retained detail report. */
export function NodeSummaryCards({
  nodes,
  now,
  onOpen,
}: NodeSummaryCardsProps): ReactElement {
  const unattached = orphanedGuests(nodes);
  return (
    <section className="chart node-summary" aria-labelledby="node-summary-title">
      <div className="node-summary__heading">
        <div>
          <h3 id="node-summary-title">Nodes at a glance</h3>
          <p>Open a node to inspect health history, services, and reports.</p>
        </div>
        <strong>{physicalNodes(nodes).length} hosts · {nodes.length - physicalNodes(nodes).length} enrolled guests</strong>
      </div>
      {unattached.length > 0 && (
        <div className="node-summary__orphan" role="status">
          <strong>{unattached.length} guest {unattached.length === 1 ? "identity needs" : "identities need"} attention</strong>
          <p>The assigned physical host is unavailable. Inspect the guest and verify its host enrollment.</p>
          {unattached.map((guest) => (
            <a key={guest.id} href={`/nodes?node=${encodeURIComponent(guest.id)}`} onClick={(event) => onOpen(event, guest)}>
              {guest.name} · open guest details
            </a>
          ))}
        </div>
      )}
      {nodes.length
        ? (
          <div className="node-summary__grid">
            {physicalNodes(nodes).map((node) => {
              const status = physicalNodeStatus(node, nodes, now);
              const guests = nodes.filter((guest) => guest.parentNodeId === node.id);
              return (
                <div key={node.id} className="node-summary__group">
                <a className="node-summary__card" href={`/nodes?node=${encodeURIComponent(node.id)}`} onClick={(event) => onOpen(event, node)}>
                  <span className={`status status--${status}`} aria-hidden="true" />
                  <span className="node-summary__identity">
                    <strong>{node.name}</strong>
                    <small>{node.projectNames?.join(" · ") || "Unassigned node"}</small>
                  </span>
                  <span className="node-summary__metrics">
                    <span>{node.diskUsedPercent}% disk</span>
                    <span>
                      {node.memoryUsedPercent === null
                        ? "Memory unavailable"
                        : `${node.memoryUsedPercent}% memory`}
                    </span>
                    {node.localGuestCount ? <span>{node.localGuestCount} Proxmox guests</span> : null}
                    <span>{node.release}</span>
                  </span>
                  <span className="node-summary__state">{status}</span>
                </a>
                {guests.map((guest) => (
                  <a key={guest.id} className="node-summary__guest" href={`/nodes?node=${encodeURIComponent(guest.id)}`} onClick={(event) => onOpen(event, guest)}>
                    <span className={`status status--${classifyNode(guest, now)}`} aria-hidden="true" />
                    <span>{guest.name} · guest VM</span>
                    <span>{classifyNode(guest, now)}</span>
                  </a>
                ))}
                </div>
              );
            })}
          </div>
        )
        : <p className="node-summary__empty">No nodes are enrolled yet.</p>}
    </section>
  );
}

/** Compare current disk and memory utilization for every node. */
export function ResourceBars(
  { nodes }: { readonly nodes: readonly FleetNode[] },
) {
  return (
    <figure className="chart chart--bars">
      <figcaption>
        <h3>Resource utilization</h3>
        <p>Current percent used; each scale runs from 0 to 100%.</p>
      </figcaption>
      <div className="resource-bars">
        {physicalNodes(nodes).map((node) => (
          <div className="resource-row" key={node.id}>
            <strong>{node.name}</strong>
            <div className="bar-pair">
              <span>Disk</span>
              <div className="bar-track">
                <i
                  className="bar-fill bar-fill--disk"
                  style={{ "--value": node.diskUsedPercent } as PercentageStyle}
                />
              </div>
              <b>{node.diskUsedPercent}%</b>
              <span>Memory</span>
              <div className="bar-track">
                <i
                  className="bar-fill bar-fill--memory"
                  style={{
                    "--value": node.memoryUsedPercent ?? 0,
                  } as PercentageStyle}
                />
              </div>
              <b>
                {node.memoryUsedPercent === null
                  ? "Unavailable"
                  : `${node.memoryUsedPercent}%`}
              </b>
            </div>
          </div>
        ))}
      </div>
    </figure>
  );
}

/** Plot disk and memory observations over time for a selected node. */
export function HistoryLine({ detail }: { readonly detail: NodeDetail }) {
  const rows = [...detail.observations].reverse();
  const width = tokens.chart.width;
  const height = tokens.chart.height;
  const padding = tokens.chart.padding;
  const points = (field: "disk_used_percent" | "memory_used_percent"): string =>
    rows.map((row, index) => {
      const x = padding +
        (index / Math.max(rows.length - 1, 1)) * (width - padding * 2);
      const value = row[field];
      if (value === null) return "";
      const y = height - padding - (value / 100) * (height - padding * 2);
      return `${x},${y}`;
    }).join(" ");
  if (rows.length === 0) {
    return (
      <div className="chart-empty">
        <h3>Utilization history</h3>
        <p>No observations are available in the retention window.</p>
      </div>
    );
  }
  return (
    <figure className="chart">
      <figcaption>
        <h3>Utilization history</h3>
        <p>{rows.length} observations, oldest to newest.</p>
      </figcaption>
      <svg
        className="line-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Disk and memory utilization history"
      >
        <line
          className="chart-axis"
          x1={padding}
          y1={height - padding}
          x2={width - padding}
          y2={height - padding}
        />
        <line
          className="chart-guide"
          x1={padding}
          y1={height / 2}
          x2={width - padding}
          y2={height / 2}
        />
        <polyline
          className="line-series line-series--disk"
          points={points("disk_used_percent")}
        />
        <polyline
          className="line-series line-series--memory"
          points={points("memory_used_percent")}
        />
      </svg>
      <div className="series-key">
        <span>
          <i className="key-line key-line--disk" />Disk
        </span>
        <span>
          <i className="key-line key-line--memory" />Memory
        </span>
      </div>
      <table className="sr-only">
        <caption>Utilization observations</caption>
        <thead>
          <tr>
            <th>Observed</th>
            <th>Disk</th>
            <th>Memory</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.sequence}>
              <td>{new Date(row.observed_at).toLocaleString()}</td>
              <td>{row.disk_used_percent}%</td>
              <td>
                {row.memory_used_percent === null
                  ? "Unavailable"
                  : `${row.memory_used_percent}%`}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </figure>
  );
}

/** Summarize retained observations as a 100% stacked health bar. */
export function HealthStack({ detail }: { readonly detail: NodeDetail }) {
  const counts = { healthy: 0, warning: 0, critical: 0 };
  for (const observation of detail.observations) {
    if (observation.health in counts) {
      counts[observation.health as keyof typeof counts] += 1;
    }
  }
  const total = Math.max(detail.observations.length, 1);
  return (
    <figure className="chart chart--stack">
      <figcaption>
        <h3>Observation mix</h3>
        <p>Health classification across retained history.</p>
      </figcaption>
      <div
        className="stacked-bar"
        role="img"
        aria-label={`${counts.healthy} healthy, ${counts.warning} warning, ${counts.critical} critical observations`}
      >
        {(["healthy", "warning", "critical"] as const).map((status) => (
          <i
            key={status}
            className={`stack-segment stack-segment--${status}`}
            style={{ "--segment": counts[status] / total } as SegmentStyle}
          />
        ))}
      </div>
      <ul className="legend legend--horizontal">
        {(["healthy", "warning", "critical"] as const).map((status) => (
          <li key={status}>
            <span className={`status status--${status}`} aria-hidden="true" />
            <span>{status}</span>
            <strong>{counts[status]}</strong>
          </li>
        ))}
      </ul>
    </figure>
  );
}

/** Produce a small status label for project node lists without relying on color alone. */
export function NodeStatusLabel(
  { node, now }: { readonly node: FleetNode; readonly now: Date },
) {
  const status = classifyNode(node, now);
  return (
    <span className="project-node-state">
      <i className={`status status--${status}`} aria-hidden="true" />
      {status}
    </span>
  );
}

/** Render an optional dependency graph with an equivalent edge list and explicit invalid states. */
export function ServiceTopology({ detail }: { readonly detail: NodeDetail }) {
  const services = detail.services.map((service) => ({
    catalogId: service.catalog_id,
    name: service.display_name,
    state: service.state,
    health: service.health,
    restartCount: service.restart_count,
    image: service.image,
    dependencies: service.dependencies,
    observedAt: service.observed_at,
  }));
  const topology = analyzeServiceTopology(services);
  if (services.length === 0) return null;
  return (
    <details className="service-topology">
      <summary>Show dependency graph</summary>
      {topology.state === "cycle"
        ? (
          <p>
            A dependency cycle was reported. The graph is shown alphabetically
            so no false startup order is implied.
          </p>
        )
        : <p>Dependencies appear before the services that require them.</p>}
      {topology.unknownDependencies.length
        ? (
          <p>
            Unknown dependencies: {topology.unknownDependencies.join(", ")}.
          </p>
        )
        : null}
      <ol>
        {topology.services.map((service) => (
          <li key={service.catalogId}>
            <strong>{service.name}</strong>
            <span>
              {service.dependencies.length
                ? ` depends on ${service.dependencies.join(", ")}`
                : " has no declared dependency"}
            </span>
            <span>{service.state} · {service.health}</span>
          </li>
        ))}
      </ol>
    </details>
  );
}
